import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, Component, ChangeDetectionStrategy, Input, HostBinding, Pipe, NgModule } from '@angular/core';
import { delayedRetry } from '@firestitch/common';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { shareReplay, switchMap, filter, take, takeUntil } from 'rxjs/operators';
import { fromFetch } from 'rxjs/fetch';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@firestitch/skeleton';
import { FsSkeletonModule } from '@firestitch/skeleton';

const arrayLikeToString = (arg) => Array.prototype.toString.call(arg);
const getTextFeature = (text, color) => {
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        ctx.textBaseline = 'top';
        ctx.font = '100px -no-font-family-here-';
        ctx.fillStyle = color;
        ctx.scale(0.01, 0.01);
        ctx.fillText(text, 0, 0);
        return ctx.getImageData(0, 0, 1, 1).data;
    }
    catch (e) {
        return false;
    }
};
const compareFeatures = (feature1, feature2) => {
    const feature1Str = arrayLikeToString(feature1);
    const feature2Str = arrayLikeToString(feature2);
    return feature1Str === feature2Str && feature1Str !== '0,0,0,0';
};
function emojSupported(text) {
    const feature1 = getTextFeature(text, '#000');
    const feature2 = getTextFeature(text, '#fff');
    return feature1 && feature2 && compareFeatures(feature1, feature2);
}

const FS_COUNTRY_CONFIG = new InjectionToken('Config for country package');

const DEFAULT_LOAD_PATH = '/assets/countries.json';
class FsCountry {
    constructor(_countryConfig) {
        this._countryConfig = _countryConfig;
        this._countriesByName = new Map();
        this._countriesByCode = new Map();
        this._countriesByCountryCode = new Map();
        this._countries$ = new BehaviorSubject(null);
        this._emojiSupported = false;
        this._ready$ = new ReplaySubject();
        this._checkIfEmojiAvailable();
        this._loadCountries();
    }
    get emojiSupported() {
        return this._emojiSupported;
    }
    get countries$() {
        return this._countries$
            .pipe(shareReplay(1));
    }
    get countries() {
        return this._countries$.getValue();
    }
    get ready$() {
        return this._ready$.asObservable();
    }
    countryByISOCode(code) {
        return this._countriesByCode.get(code);
    }
    countryByName(name) {
        return this._countriesByName.get(name);
    }
    countriesByCountryCode(code) {
        return this._countriesByCountryCode.get(code);
    }
    _loadCountries() {
        var _a;
        fromFetch(((_a = this._countryConfig) === null || _a === void 0 ? void 0 : _a.countriesPath) || DEFAULT_LOAD_PATH)
            .pipe(switchMap((response) => response.json()), delayedRetry(2000, 3))
            .subscribe({
            next: (data) => {
                this._countries$.next(data);
                this._processCountries();
                this._ready$.next(true);
            },
            error: (e) => {
                throw new Error(`Countries list can not be loaded. ${e}`);
            },
        });
    }
    _processCountries() {
        this.countries.forEach((country) => {
            this._countriesByName.set(country.name, country);
            this._countriesByCode.set(country.isoCode, country);
            if (this._countriesByCountryCode.has(country.countryCode)) {
                this._countriesByCountryCode
                    .get(country.countryCode)
                    .push(country);
            }
            else {
                this._countriesByCountryCode.set(country.countryCode, [country]);
            }
        });
    }
    _checkIfEmojiAvailable() {
        this._emojiSupported = emojSupported('🇺🇸');
    }
}
FsCountry.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountry, deps: [{ token: FS_COUNTRY_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
FsCountry.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountry, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountry, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_COUNTRY_CONFIG]
                }] }]; } });

const DEFAULT_FLAGS_PATH = '/assets/country-flags/';

class FsCountryFlagComponent {
    constructor(countryService, _cdRef, _config) {
        this.countryService = countryService;
        this._cdRef = _cdRef;
        this._config = _config;
        this.width = 16;
        this._destroy$ = new Subject();
    }
    set code(value) {
        this._countryISOCode = value === null || value === void 0 ? void 0 : value.trim().toUpperCase();
        this._updateFlag();
        this._updateFlagPath();
    }
    ;
    get emojiSupported() {
        return this.countryService.emojiSupported;
    }
    get countryFlagPath() {
        return this._countryFlagPath;
    }
    get countryEmoji() {
        return this._countryEmoji;
    }
    ngOnInit() {
        if (this.countryService.emojiSupported) {
            this.countryService.ready$
                .pipe(filter((value) => {
                return value;
            }), take(1), takeUntil(this._destroy$))
                .subscribe(() => {
                this._updateFlag();
                this._cdRef.markForCheck();
            });
        }
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _updateFlag() {
        var _a;
        this._countryEmoji = (_a = this.countryService.countryByISOCode(this._countryISOCode)) === null || _a === void 0 ? void 0 : _a.emoji;
    }
    _updateFlagPath() {
        var _a;
        if (!this.emojiSupported) {
            const path = ((_a = this._config) === null || _a === void 0 ? void 0 : _a.countriesPath) || DEFAULT_FLAGS_PATH;
            this._countryFlagPath = `${path}${this._countryISOCode}.svg`;
        }
    }
}
FsCountryFlagComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryFlagComponent, deps: [{ token: FsCountry }, { token: i0.ChangeDetectorRef }, { token: FS_COUNTRY_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Component });
FsCountryFlagComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsCountryFlagComponent, selector: "fs-country-flag", inputs: { code: "code", width: "width" }, ngImport: i0, template: "<ng-container *ngIf=\"emojiSupported && countryEmoji\">\n  {{ countryEmoji }}\n</ng-container>\n\n<ng-container *ngIf=\"!emojiSupported\">\n  <img [src]=\"countryFlagPath\" alt=\"\" [style.width]=\"width + 'px'\">\n</ng-container>\n", directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryFlagComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-country-flag',
                    templateUrl: './country-flag.component.html',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsCountry }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_COUNTRY_CONFIG]
                }] }]; }, propDecorators: { code: [{
                type: Input
            }], width: [{
                type: Input
            }] } });

class FsCountryComponent {
    constructor(countryService, _cdRef, _config) {
        this.countryService = countryService;
        this._cdRef = _cdRef;
        this._config = _config;
        this.showIcon = true;
        this.showName = true;
        this.width = 16;
        this._destroy$ = new Subject();
    }
    get emojiSupported() {
        return this.countryService.emojiSupported;
    }
    get countryISOCode() {
        return this._countryISOCode;
    }
    get countryFlagPath() {
        return this._countryFlagPath;
    }
    get country() {
        return this._country;
    }
    ngOnInit() {
        this.countryService.ready$
            .pipe(take(1), takeUntil(this._destroy$))
            .subscribe(() => {
            this._updateFlag();
            this._updateFlagPath();
            this._cdRef.markForCheck();
        });
    }
    ngOnChanges(changes) {
        var _a;
        if (changes.code) {
            this._countryISOCode = (_a = this.code) === null || _a === void 0 ? void 0 : _a.trim().toUpperCase();
            this._updateFlag();
            this._updateFlagPath();
        }
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _updateFlag() {
        this._country = this.countryService.countryByISOCode(this._countryISOCode);
    }
    _updateFlagPath() {
        var _a;
        if (this.emojiSupported) {
            return;
        }
        const path = ((_a = this._config) === null || _a === void 0 ? void 0 : _a.countriesPath) || DEFAULT_FLAGS_PATH;
        this._countryFlagPath = `${path}${this._countryISOCode}.svg`;
    }
}
FsCountryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryComponent, deps: [{ token: FsCountry }, { token: i0.ChangeDetectorRef }, { token: FS_COUNTRY_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Component });
FsCountryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsCountryComponent, selector: "fs-country", inputs: { code: "code", showIcon: "showIcon", showName: "showName", region: "region", width: "width" }, host: { properties: { "class.show-icon": "this.showIcon", "class.show-name": "this.showName" } }, usesOnChanges: true, ngImport: i0, template: "<ng-container *fsSkeleton=\"countryService.ready$ | async; pattern: [100]\">\n  <ng-container *ngIf=\"country\">\n    <ng-container *ngIf=\"emojiSupported else svgCountry\">\n      <ng-container *ngIf=\"showIcon\">{{ country.emoji }}</ng-container> \n      <ng-container *ngTemplateOutlet=\"name\"></ng-container>\n    </ng-container>\n\n    <ng-template #svgCountry>\n      <ng-container *ngIf=\"country\">\n        <img *ngIf=\"showIcon\" [src]=\"countryFlagPath\" [alt]=\"countryISOCode\" [style.width]=\"width + 'px'\">\n        <ng-container *ngTemplateOutlet=\"name\"></ng-container>\n      </ng-container>\n    </ng-template>\n  </ng-container>\n</ng-container>\n\n<ng-template #name>\n  <span *ngIf=\"showName\" class=\"name\">{{ country.name }}{{ region ? ', ' + region : '' }}</span>\n</ng-template>", styles: [":host.show-name.show-icon{flex-direction:row;align-items:center;display:flex}:host.show-name.show-icon .name{margin-left:4px}\n"], directives: [{ type: i2.FsSkeletonContentDirective, selector: "[fsSkeleton]", inputs: ["fsSkeleton", "fsSkeletonPattern"] }, { type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], pipes: { "async": i3.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-country',
                    templateUrl: './country.component.html',
                    styleUrls: ['./country.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsCountry }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_COUNTRY_CONFIG]
                }] }]; }, propDecorators: { code: [{
                type: Input
            }], showIcon: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.show-icon']
            }], showName: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.show-name']
            }], region: [{
                type: Input
            }], width: [{
                type: Input
            }] } });

class FsCountryPhoneCodeComponent extends FsCountryComponent {
    constructor() {
        super(...arguments);
        this.showCode = true;
    }
}
FsCountryPhoneCodeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryPhoneCodeComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
FsCountryPhoneCodeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsCountryPhoneCodeComponent, selector: "fs-country-phone-code", inputs: { showCode: "showCode" }, host: { properties: { "class.show-code": "this.showCode" } }, usesInheritance: true, ngImport: i0, template: "<ng-container *fsSkeleton=\"countryService.ready$ | async; pattern: [100]\">\n  <ng-container *ngIf=\"country\">\n    <ng-container *ngIf=\"emojiSupported else svgCountry\">\n      <ng-container *ngIf=\"showIcon\">{{ country.emoji }}</ng-container>\n      <span class=\"code\" *ngIf=\"showCode\">+{{ country.countryCode }}</span>\n    </ng-container>\n\n    <ng-template #svgCountry>\n      <ng-container *ngIf=\"country\">\n        <img *ngIf=\"showIcon\" [src]=\"countryFlagPath\" [alt]=\"countryISOCode\" [style.width]=\"width + 'px'\">\n        <span class=\"code\" *ngIf=\"showCode\">+{{ country.countryCode }}</span>\n      </ng-container>\n    </ng-template>\n  </ng-container>\n</ng-container>\n", styles: [":host.show-code.show-icon{flex-direction:row;align-items:center}:host.show-code.show-icon .code{margin-left:3px}\n"], directives: [{ type: i2.FsSkeletonContentDirective, selector: "[fsSkeleton]", inputs: ["fsSkeleton", "fsSkeletonPattern"] }, { type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "async": i3.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryPhoneCodeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-country-phone-code',
                    templateUrl: './country-phone-code.component.html',
                    styleUrls: ['./country-phone-code.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { showCode: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.show-code']
            }] } });

class FsCountryCallingCodePipe {
    constructor(_countryService) {
        this._countryService = _countryService;
    }
    transform(code, includeEmoji = true) {
        const country = this._countryService.countryByISOCode(code);
        if (!country) {
            return '';
        }
        if (includeEmoji) {
            return `${country.emoji} +${country.countryCode}`;
        }
        else {
            return `+${country.countryCode}`;
        }
    }
}
FsCountryCallingCodePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryCallingCodePipe, deps: [{ token: FsCountry }], target: i0.ɵɵFactoryTarget.Pipe });
FsCountryCallingCodePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryCallingCodePipe, name: "fsCountryCallingCode" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryCallingCodePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'fsCountryCallingCode' }]
        }], ctorParameters: function () { return [{ type: FsCountry }]; } });

class FsCountryModule {
    static forRoot(config) {
        return {
            ngModule: FsCountryModule,
            providers: [
                {
                    provide: FS_COUNTRY_CONFIG,
                    useValue: config,
                }
            ]
        };
    }
}
FsCountryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsCountryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryModule, declarations: [FsCountryCallingCodePipe,
        FsCountryFlagComponent,
        FsCountryPhoneCodeComponent,
        FsCountryComponent], imports: [CommonModule,
        FsSkeletonModule], exports: [FsCountryCallingCodePipe,
        FsCountryFlagComponent,
        FsCountryPhoneCodeComponent,
        FsCountryComponent] });
FsCountryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryModule, providers: [], imports: [[
            CommonModule,
            FsSkeletonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsCountryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FsSkeletonModule,
                    ],
                    declarations: [
                        FsCountryCallingCodePipe,
                        FsCountryFlagComponent,
                        FsCountryPhoneCodeComponent,
                        FsCountryComponent,
                    ],
                    exports: [
                        FsCountryCallingCodePipe,
                        FsCountryFlagComponent,
                        FsCountryPhoneCodeComponent,
                        FsCountryComponent,
                    ],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FS_COUNTRY_CONFIG, FsCountry, FsCountryCallingCodePipe, FsCountryComponent, FsCountryFlagComponent, FsCountryModule, FsCountryPhoneCodeComponent };
